import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { RefreshCw, Zap, Clock, CheckCircle2, XCircle } from 'lucide-react';

import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import MetricsCards from '../components/growth-os/MetricsCards';
import AuditFindingsList from '../components/growth-os/AuditFindingsList';
import DailyReportViewer from '../components/growth-os/DailyReportViewer';
import ActionQueueTable from '../components/growth-os/ActionQueueTable';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  // Auth uses httpOnly cookies — axios sends them via `withCredentials`.
  // Kept as no-op wrapper for compatibility with any future header-based override.
  return {};
};

export default function GrowthOSDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [latestAudit, setLatestAudit] = useState(null);
  const [latestReport, setLatestReport] = useState(null);
  const [actions, setActions] = useState([]);

  // Trigger loading states
  const [auditRunning, setAuditRunning] = useState(false);
  const [reportRunning, setReportRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = authHeader();
      const [dashRes, auditRes, reportRes, actionsRes] = await Promise.all([
        axios.get(`${API}/api/growth-os/dashboard`, { headers, withCredentials: true }),
        axios.get(`${API}/api/growth-os/audits/latest`, { headers, withCredentials: true }),
        axios.get(`${API}/api/growth-os/reports/latest?type=daily`, { headers, withCredentials: true }),
        axios.get(`${API}/api/growth-os/actions/pending?limit=200`, { headers, withCredentials: true }),
      ]);
      setDashboard(dashRes.data);
      setLatestAudit(auditRes.data?.audit || null);
      setLatestReport(reportRes.data?.report || null);
      setActions(actionsRes.data?.actions || []);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error('Session expired — दोबारा login करें');
        navigate('/admin/login');
      } else {
        toast.error('Data load failed: ' + (err?.message || 'unknown'));
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const triggerAudit = async () => {
    setAuditRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/api/growth-os/trigger/audit`,
        {},
        { headers: authHeader(), timeout: 60000, withCredentials: true },
      );
      toast.success(
        `Audit complete: ${data.routes_scanned} routes · ${(data.summary?.critical || 0) + (data.summary?.high || 0)} high-priority issues · ${data.new_actions} new actions`
      );
      await load();
    } catch (err) {
      toast.error('Audit failed: ' + (err?.response?.data?.detail || err?.message));
    } finally {
      setAuditRunning(false);
    }
  };

  const triggerReport = async ({ send = false } = {}) => {
    setReportRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/api/growth-os/trigger/report?type=daily&send=${send}`,
        {},
        { headers: authHeader(), timeout: 90000, withCredentials: true },
      );
      toast.success(
        `Report generated${send ? ' + WhatsApp sent' : ' (preview only)'}: ${data.report_id}`
      );
      await load();
    } catch (err) {
      toast.error('Report generation failed: ' + (err?.response?.data?.detail || err?.message));
    } finally {
      setReportRunning(false);
    }
  };

  const sched = dashboard?.scheduler;
  const nextJobs = sched?.jobs || [];
  const integrations = dashboard?.integrations || {};

  return (
    <div className="min-h-screen bg-background text-text-main">
      <SEO
        title="Growth OS Dashboard | ViratAstro Admin"
        description="AI Growth Manager — daily audits, metrics, reports, and pending actions."
        path="/admin/growth-os"
      />
      <Header />

      <main className="container mx-auto px-4 max-w-7xl pt-24 pb-16">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-1">
              Growth OS · Phase 1
            </div>
            <h1 className="font-heading text-3xl md:text-4xl text-text-main">
              🚀 ViratAstro <span className="text-gold">Growth Dashboard</span>
            </h1>
            <p className="text-sm text-text-muted mt-1">
              AI-powered daily audits, business metrics, and Hindi WhatsApp reports at 10 PM IST.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="growth-os-refresh-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gold/50 text-gold font-semibold text-sm hover:bg-gold hover:text-[#0a0820] disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Scheduler + integrations status strip */}
        {sched && (
          <div className="mb-6 rounded-xl bg-[#0a0820]/60 border border-gold/20 px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
            <span className="inline-flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-emerald-400" />
              <strong className="text-emerald-400">Scheduler {sched.running ? 'Running' : 'Stopped'}</strong>
              <span className="text-text-muted"> ({sched.timezone})</span>
            </span>
            {nextJobs.slice(0, 4).map((j) => (
              <span key={j.id} className="inline-flex items-center gap-1.5 text-text-muted">
                <Clock className="w-3 h-3 text-gold/70" />
                {j.id.replace('growth_os_', '').replace('_', ' ')}: {' '}
                <strong className="text-text-main">
                  {j.next_run_time
                    ? new Date(j.next_run_time).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false })
                    : '—'}
                </strong>
              </span>
            ))}
            <span className="ml-auto flex items-center gap-3">
              <span className="inline-flex items-center gap-1">
                {integrations.ga4_configured ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                ) : (
                  <XCircle className="w-3.5 h-3.5 text-yellow-400" />
                )}
                GA4
              </span>
              <span className="inline-flex items-center gap-1">
                {integrations.gsc_configured ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                ) : (
                  <XCircle className="w-3.5 h-3.5 text-yellow-400" />
                )}
                GSC
              </span>
              <span className="inline-flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> WhatsApp
              </span>
            </span>
          </div>
        )}

        {/* Metrics Cards */}
        <section className="mb-8" data-testid="growth-os-metrics-section">
          <div className="text-xs uppercase tracking-widest text-gold/70 font-semibold mb-3">
            आज का Snapshot
          </div>
          <MetricsCards today={dashboard?.today} yesterday={dashboard?.yesterday} />
        </section>

        {/* Daily Report */}
        <section className="mb-8" data-testid="growth-os-report-section">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-gold/70 font-semibold">
              Latest Daily Report
            </div>
            {latestReport && (
              <button
                onClick={() => triggerReport({ send: true })}
                disabled={reportRunning}
                data-testid="growth-os-send-whatsapp-btn"
                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold disabled:opacity-50 transition-colors"
              >
                {reportRunning ? '⏳ भेज रहे हैं…' : '📱 अभी WhatsApp पर भेजें'}
              </button>
            )}
          </div>
          <DailyReportViewer
            report={latestReport}
            onGenerate={() => triggerReport({ send: false })}
            generating={reportRunning}
          />
        </section>

        {/* Audit Findings */}
        <section className="mb-8" data-testid="growth-os-audit-section">
          <div className="text-xs uppercase tracking-widest text-gold/70 font-semibold mb-3">
            🔍 Latest Audit Findings
          </div>
          <AuditFindingsList audit={latestAudit} onTrigger={triggerAudit} loading={auditRunning} />
        </section>

        {/* Action Queue */}
        <section className="mb-4" data-testid="growth-os-actions-section">
          <div className="text-xs uppercase tracking-widest text-gold/70 font-semibold mb-3">
            ✅ Proposed Actions ({actions.length} pending)
          </div>
          <ActionQueueTable actions={actions} onStatusChange={load} />
        </section>

        <p className="text-[11px] text-text-muted text-center mt-6">
          ⚠️ Phase-1 Read-Only Mode · कोई भी action बिना अनुमति execute नहीं होगा · Phase-2 में Approve/Reject आएगा
        </p>
      </main>

      <Footer />
    </div>
  );
}
